// SGNL-150 — POC Explorer tab.
//
// SE / POC owner workflow:
//   1. Page lists all active POCs from `pocs.json` (instant render).
//   2. Click "Find matching opportunities" on a row → backend runs
//      the incremental scan (RAG top-100 + LLM-evaluate-the-delta) →
//      ranked opps table appears.
//   3. Subsequent scans of the same POC are nearly free (cache hit).
//
// UI states per POC row:
//   - idle (default)
//   - scanning (button disabled, spinner)
//   - results (table appended in-row)
//   - error (retry button)

import { fetchPocCatalog, scanPoc } from "../api";
import { esc, fmtMoney } from "../lib/format";
import type { OppMatch, PocCatalogRow, PocScanResult } from "../types";

// Module-level cache of the catalog so flipping tabs doesn't re-fetch.
let _catalogPromise: Promise<PocCatalogRow[]> | null = null;

function fetchCatalogCached(): Promise<PocCatalogRow[]> {
  if (!_catalogPromise) {
    _catalogPromise = fetchPocCatalog().catch((err) => {
      _catalogPromise = null;
      throw err;
    });
  }
  return _catalogPromise;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return esc(s);
  return d.toISOString().slice(0, 10);
}

function renderOutcomeChip(m: OppMatch): string {
  // Mirror SGNL-144 Similar deals chip palette.
  if (m.is_won === true) {
    return `<span class="chip blue" aria-label="Closed Won"><span aria-hidden="true">✓</span> Won</span>`;
  }
  if (m.is_closed === true && m.is_won === false) {
    return `<span class="chip coral" aria-label="Closed Lost"><span aria-hidden="true">✗</span> Lost</span>`;
  }
  const stageLabel = m.stage ?? "Open";
  return `<span class="chip indigo" aria-label="${esc(stageLabel)}">${esc(stageLabel)}</span>`;
}

function renderOppMatchRow(m: OppMatch): string {
  const oppHref = `#opp/${encodeURIComponent(m.opp_id)}`;
  const amount = m.amount !== null ? fmtMoney(m.amount) : "—";
  const account = m.account_name ?? "—";
  const matchChip = `<span class="chip indigo" title="LLM-assessed strength of fit, 0-100">${m.match_pct}%</span>`;
  const excerpt = m.summary_excerpt
    ? `<p class="muted" style="margin:6px 0 0;">${esc(m.summary_excerpt)}</p>`
    : "";
  return `
    <li class="poc-opp-match-row" data-testid="poc-opp-match-row" data-opp-id="${esc(m.opp_id)}" style="border-top:1px solid var(--line); padding:10px 0;">
      <div style="display:flex; gap:8px; align-items:baseline; flex-wrap:wrap;">
        <a href="${oppHref}"><strong>${esc(m.opp_name)}</strong></a>
        ${renderOutcomeChip(m)}
        ${matchChip}
      </div>
      <div class="muted" style="font-size:0.9em; margin-top:4px; display:flex; gap:8px; flex-wrap:wrap;">
        <span>${esc(account)}</span>
        <span>·</span>
        <span>${amount}</span>
      </div>
      <p style="margin:6px 0 0; font-size:0.95em;"><strong>Why:</strong> ${esc(m.rationale)}</p>
      ${excerpt}
    </li>`;
}

function renderScanResults(result: PocScanResult): string {
  if (result.matches.length === 0) {
    return `
      <div class="poc-scan-results" data-testid="poc-scan-empty" style="margin-top:12px;">
        <p class="empty-bucket">No matching opportunities found in the corpus. The matcher considered ${result.total_candidates} candidates and didn't find a meaningful fit.</p>
      </div>`;
  }
  // SGNL-194: model name no longer rendered at user-facing surfaces.
  const meta = `${result.newly_evaluated} new · ${result.reused_from_cache} reused from cache · ${result.total_candidates} candidates`;
  return `
    <div class="poc-scan-results" data-testid="poc-scan-results" style="margin-top:12px;">
      <p class="muted" style="margin:0 0 6px; font-size:0.9em;">${meta}</p>
      <ul class="poc-opp-match-list" data-testid="poc-opp-match-list" style="list-style:none; padding:0; margin:0;">
        ${result.matches.map(renderOppMatchRow).join("\n")}
      </ul>
    </div>`;
}

function renderPocCard(poc: PocCatalogRow): string {
  const url = poc.url
    ? `<a href="${esc(poc.url)}" target="_blank" rel="noopener noreferrer">${esc(poc.url)}</a>`
    : `<span class="muted">No external URL</span>`;
  const techList = poc.technologies.length
    ? poc.technologies.slice(0, 12).map((t) => `<span class="chip indigo" style="margin-right:4px;">${esc(t)}</span>`).join(" ")
    : `<span class="muted">none listed</span>`;
  const techMore = poc.technologies.length > 12 ? `<span class="muted">+${poc.technologies.length - 12} more</span>` : "";
  return `
    <article class="poc-card" data-testid="poc-card" data-poc-id="${esc(poc.id)}" style="border:1px solid var(--line); border-radius:6px; padding:14px; margin:12px 0;">
      <header style="display:flex; justify-content:space-between; align-items:baseline; gap:12px; flex-wrap:wrap;">
        <h3 style="margin:0; font-size:1.05em;">${esc(poc.name)} <span class="muted" style="font-size:0.8em; font-weight:normal;">(${esc(poc.id)})</span></h3>
        <button class="action-link" data-action="scan-poc" data-poc-id="${esc(poc.id)}" data-testid="scan-poc-button" type="button">Find matching opportunities</button>
      </header>
      <p style="margin:8px 0;">${esc(poc.description)}</p>
      <p style="margin:8px 0 4px;"><strong>Technologies:</strong> ${techList} ${techMore}</p>
      <p style="margin:4px 0;"><strong>Ideal customer:</strong> <span class="muted">${esc(poc.ideal_customer_profile || "—")}</span></p>
      <p class="muted" style="margin:4px 0; font-size:0.85em;">Owner: ${esc(poc.owner)} · Updated ${fmtDate(poc.last_updated)} · Link: ${url}</p>
      <div class="poc-scan-slot" data-testid="poc-scan-slot" data-poc-id="${esc(poc.id)}"></div>
    </article>`;
}

function renderShell(catalog: PocCatalogRow[]): string {
  if (catalog.length === 0) {
    return `
      <div class="tab-content" data-testid="poc-explorer-empty">
        <div class="tab-header"><h2>POC Explorer</h2></div>
        <p class="empty-bucket">No active POCs in the catalog. Add entries to <code>backend/app/pocs/pocs.json</code> + redeploy.</p>
      </div>`;
  }
  return `
    <div class="tab-content" data-testid="poc-explorer">
      <div class="tab-header">
        <h2>POC Explorer</h2>
        <span class="row-count">${catalog.length} POC${catalog.length === 1 ? "" : "s"}</span>
      </div>
      <p class="muted">Pick a POC and find which open opportunities it could land on. Each scan considers the closest 100 opps by semantic similarity, then ranks them with Sonnet. Re-scans of the same POC are nearly free (cached).</p>
      <div data-testid="poc-cards">
        ${catalog.map(renderPocCard).join("\n")}
      </div>
    </div>`;
}

function renderScanLoading(): string {
  return `
    <div class="poc-scan-loading" data-testid="poc-scan-loading" style="margin-top:12px;">
      <p class="muted">Scanning — this can take 30-60s on a first scan, near-instant on re-scans…</p>
    </div>`;
}

function _normaliseErrorForDisplay(msg: string): string {
  // Code-review M5: don't reflect the raw fetch error message into the
  // DOM verbatim. `fetch` errors can carry the full URL + server-side
  // body fragments — internal endpoint shape leaking to the browser
  // is low-risk for an internal tool but worth scrubbing. Extract the
  // HTTP status if present; otherwise show a generic message.
  const m = msg.match(/returned (\d{3})/);
  if (m) return `HTTP ${m[1]}`;
  // Unknown shape — give the user something rather than the raw text.
  return "transport error";
}

function renderScanError(msg: string): string {
  const display = _normaliseErrorForDisplay(msg);
  return `
    <div class="poc-scan-error" data-testid="poc-scan-error" style="margin-top:12px;">
      <p class="muted">Couldn't run the scan (${esc(display)}). Try again, or refresh the page.</p>
      <p><button class="action-link" data-action="retry-scan-poc" type="button">Retry</button></p>
    </div>`;
}

// Single AbortController per host so re-mounts don't stack listeners
// (same pattern as SGNL-146 / SGNL-149).
const _hostAbort = new WeakMap<HTMLElement, AbortController>();

function bindScanButtons(host: HTMLElement): void {
  _hostAbort.get(host)?.abort();
  const controller = new AbortController();
  _hostAbort.set(host, controller);

  // Monotonic per-POC token so a slow first scan doesn't overwrite a
  // newer scan's result if the user re-clicks (or clicks elsewhere).
  const tokens: Record<string, number> = {};

  async function runScan(pocId: string): Promise<void> {
    const token = (tokens[pocId] ?? 0) + 1;
    tokens[pocId] = token;
    const slot = host.querySelector<HTMLElement>(
      `[data-testid="poc-scan-slot"][data-poc-id="${pocId}"]`,
    );
    if (!slot) return;
    slot.innerHTML = renderScanLoading();
    // Disable the button while the scan is in flight.
    const btn = host.querySelector<HTMLButtonElement>(
      `[data-action="scan-poc"][data-poc-id="${pocId}"]`,
    );
    if (btn) btn.disabled = true;
    try {
      const result = await scanPoc(pocId);
      if (tokens[pocId] !== token) return; // newer scan owns the slot
      slot.innerHTML = renderScanResults(result);
    } catch (err) {
      if (tokens[pocId] !== token) return;
      const msg = err instanceof Error ? err.message : String(err);
      slot.innerHTML = renderScanError(msg);
    } finally {
      const btnNow = host.querySelector<HTMLButtonElement>(
        `[data-action="scan-poc"][data-poc-id="${pocId}"]`,
      );
      if (btnNow) btnNow.disabled = false;
    }
  }

  host.addEventListener(
    "click",
    (e) => {
      // Element instance check — `closest()` is only defined on Element
      // (text-node targets can fire click events in some browsers).
      const target = e.target;
      if (!(target instanceof Element)) return;

      const scanBtn = target.closest<HTMLButtonElement>(
        '[data-action="scan-poc"]',
      );
      if (scanBtn) {
        e.preventDefault();
        const pocId = scanBtn.dataset.pocId ?? "";
        if (!pocId) return;
        void runScan(pocId);
        return;
      }

      const retryBtn = target.closest<HTMLButtonElement>(
        '[data-action="retry-scan-poc"]',
      );
      if (retryBtn) {
        e.preventDefault();
        // Recover pocId from the nearest poc-card ancestor.
        const card = retryBtn.closest<HTMLElement>('[data-testid="poc-card"]');
        const pocId = card?.dataset.pocId ?? "";
        if (pocId) void runScan(pocId);
      }
    },
    { signal: controller.signal },
  );
}

export async function renderPocExplorerTab(host: HTMLElement): Promise<void> {
  host.innerHTML = `
    <div class="tab-content" data-testid="poc-explorer-loading">
      <div class="tab-header"><h2>POC Explorer</h2></div>
      <p class="muted">Loading POC catalog…</p>
    </div>`;
  try {
    const catalog = await fetchCatalogCached();
    host.innerHTML = renderShell(catalog);
    bindScanButtons(host);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    host.innerHTML = `
      <div class="tab-content" data-testid="poc-explorer-error">
        <div class="tab-header"><h2>POC Explorer</h2></div>
        <p class="muted">Couldn't load the POC catalog (${esc(msg)}).</p>
      </div>`;
  }
}

// Test seam: clears the module-level catalog cache so Vitest tests
// can re-fetch deterministically.
export function _resetPocCatalogCache(): void {
  _catalogPromise = null;
}
